import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { Box, LinearProgress } from '@mui/material';
import {
  getCCReport,
  getCCReportInvoices,
  createCCReportInvoice,
  deleteCCReportInvoice,
} from '../components/construction-cost-report/construction-cost-report.api';
import keys from '../common/react-query/data-keys';
import { handleError } from '../common/utils/util.errors';
import InvoicePage from './InvoicePage';
import { toast } from 'react-toastify';
import { CreateCCReportInvoiceDTO } from '../components/construction-cost-report/construction-cost-report.type';
import { BaseInvoiceDTO, CreateInvoiceDTO } from '../components/invoice/invoice.type';
import { useAppContext } from '../common/AppContext';
import { exportInvoiceAsPDF } from '../components/invoice/invoice.util';

const CCReportInvoicePage: React.FC = () => {
  const { reportId } = useParams();
  const queryClient = useQueryClient();
  const { setPageHeaderText } = useAppContext();
  const {
    data: report,
    isLoading: isLoadingReport,
    isError: isErrorInReport,
    error: errorInReport,
  } = useQuery([keys.ccReport, reportId], () => getCCReport(reportId!), {
    enabled: Boolean(reportId),
  });

  const {
    data: invoices = [],
    isLoading: isLoadingInvoices,
    isError: isErrorInvoices,
    error: errorInvoices,
  } = useQuery([keys.ccReport, reportId, 'invoices'], () => getCCReportInvoices(reportId!), {
    enabled: Boolean(reportId),
  });

  useEffect(() => {
    if (report?.name) {
      setPageHeaderText(`Invoice / ${report.name}`);
    } else {
      setPageHeaderText('Invoice');
    }

    // Cleanup function to reset page header when component unmounts
    return () => {
      setPageHeaderText('');
    };
  }, [report?.name, setPageHeaderText]);

  if (isLoadingReport || isLoadingInvoices) return <LinearProgress />;
  if (isErrorInReport) {
    handleError(errorInReport);
    return null;
  }
  if (isErrorInvoices) {
    handleError(errorInvoices);
    return null;
  }

  if (!report) {
    return <Box>Report not found</Box>;
  }

  const handleExportInvoice = async (invoiceData: CreateInvoiceDTO) => {
    try {
      // Check for duplicate invoice numbers
      const trimmedInvoiceNumber = invoiceData.invoice.number.trim();
      const isDuplicate = invoices.some(
        existingInvoice => existingInvoice.invoice.number.trim().toLowerCase() === trimmedInvoiceNumber.toLowerCase(),
      );

      if (isDuplicate) {
        toast.error('Invoice number already exists');
        return;
      }

      // Transform the invoice data to match the backend DTO
      const createInvoiceDto: CreateCCReportInvoiceDTO = {
        ccReportId: report.id,
        invoice: {
          number: invoiceData.invoice.number,
          invoiceDate: invoiceData.invoice.invoiceDate,
          invoiceAmount: invoiceData.invoice.invoiceAmount,
          paidAmount: invoiceData.invoice.paidAmount,
        },
        recepient: invoiceData.recepient,
        recepientAddress: invoiceData.recepientAddress,
        projectName: invoiceData.projectName,
        projectAddress: {
          street: invoiceData.projectAddress?.street || '',
          city: invoiceData.projectAddress?.city || '',
          state: invoiceData.projectAddress?.state?.toUpperCase() || '',
          postalCode: invoiceData.projectAddress?.postalCode || '',
        },
        service: 'Construction Cost Review',
      };

      // Save the invoice to the backend
      const savedInvoice = await createCCReportInvoice(createInvoiceDto);

      // Invalidate the invoices query to refresh the list
      await queryClient.invalidateQueries([keys.ccReport, reportId, 'invoices']);

      // Export PDF using the utility function
      await exportInvoiceAsPDF(
        invoiceData,
        {
          projectNumber: report.projectNumber,
          projectTitle: report.name,
          clientName: report.clientName || '',
          clientAddress: {
            street: report.clientAddress || '',
            city: '',
            state: '',
            postalCode: '',
          },
          projectAddress: {
            street: report.projectAddress?.street || '',
            city: report.projectAddress?.city || '',
            state: report.projectAddress?.state?.toUpperCase() || '',
            postalCode: report.projectAddress?.postalCode || '',
          },
        },
        report.projectNumber,
      );
    } catch (error) {
      handleError(error);
      toast.error('Failed to save invoice');
    }
  };

  // Function to check for duplicate invoice numbers
  const checkDuplicateInvoice = (invoiceNumber: string): boolean => {
    const trimmedInvoiceNumber = invoiceNumber.trim();
    return invoices.some(existingInvoice => existingInvoice.invoice.number.trim().toLowerCase() === trimmedInvoiceNumber.toLowerCase());
  };

  // Function to export and save new invoice (for CreateInvoice form)
  const handleExportAndSave = async (invoiceData: CreateInvoiceDTO) => {
    try {
      // Check for duplicate invoice numbers
      if (checkDuplicateInvoice(invoiceData.invoice.number)) {
        toast.error('Invoice number already exists');
        return;
      }

      // Transform the invoice data to match the backend DTO
      const createInvoiceDto: CreateCCReportInvoiceDTO = {
        ccReportId: report.id,
        invoice: {
          number: invoiceData.invoice.number,
          invoiceDate: invoiceData.invoice.invoiceDate,
          invoiceAmount: invoiceData.invoice.invoiceAmount,
          paidAmount: invoiceData.invoice.paidAmount,
        },
        recepient: invoiceData.recepient,
        recepientAddress: invoiceData.recepientAddress,
        projectName: invoiceData.projectName,
        projectAddress: {
          street: invoiceData.projectAddress?.street || '',
          city: invoiceData.projectAddress?.city || '',
          state: invoiceData.projectAddress?.state?.toUpperCase() || '',
          postalCode: invoiceData.projectAddress?.postalCode || '',
        },
        service: 'Construction Cost Review',
      };

      // Save the invoice to the backend
      const savedInvoice = await createCCReportInvoice(createInvoiceDto);

      // Invalidate the invoices query to refresh the list
      await queryClient.invalidateQueries([keys.ccReport, reportId, 'invoices']);

      // Export PDF using the utility function
      await exportInvoiceAsPDF(
        invoiceData,
        {
          projectNumber: report.projectNumber,
          projectTitle: report.name,
          clientName: report.clientName || '',
          clientAddress: {
            street: report.clientAddress || '',
            city: '',
            state: '',
            postalCode: '',
          },
          projectAddress: {
            street: report.projectAddress?.street || '',
            city: report.projectAddress?.city || '',
            state: report.projectAddress?.state?.toUpperCase() || '',
            postalCode: report.projectAddress?.postalCode || '',
          },
        },
        report.projectNumber,
      );
    } catch (error) {
      handleError(error);
      toast.error('Failed to save invoice');
    }
  };

  // Function to export only (for past invoices)
  const handleExport = async (invoiceData: CreateInvoiceDTO) => {
    try {
      // Export PDF using the utility function
      await exportInvoiceAsPDF(
        invoiceData,
        {
          projectNumber: report.projectNumber,
          projectTitle: report.name,
          clientName: report.clientName || '',
          clientAddress: {
            street: report.clientAddress || '',
            city: '',
            state: '',
            postalCode: '',
          },
          projectAddress: {
            street: report.projectAddress?.street || '',
            city: report.projectAddress?.city || '',
            state: report.projectAddress?.state?.toUpperCase() || '',
            postalCode: report.projectAddress?.postalCode || '',
          },
        },
        report.projectNumber,
      );
    } catch (error) {
      handleError(error);
      toast.error('Failed to export invoice');
    }
  };

  const handleDeleteInvoice = async (invoiceId: string) => {
    try {
      await deleteCCReportInvoice(invoiceId);

      // Invalidate the invoices query to refresh the list
      await queryClient.invalidateQueries([keys.ccReport, reportId, 'invoices']);

      toast.success('Invoice deleted successfully!');
    } catch (error) {
      handleError(error);
      toast.error('Failed to delete invoice');
    }
  };

  // Transform the report data to match the expected interface
  const reportData = {
    id: report.id,
    name: report.name,
    project: {
      projectNumber: report.projectNumber,
      projectTitle: report.name,
      clientName: report.clientName || '',
      clientAddress: {
        street: report.clientAddress || '',
        city: '',
        state: '',
        postalCode: '',
      },
      projectAddress: {
        street: report.projectAddress?.street || '',
        city: report.projectAddress?.city || '',
        state: report.projectAddress?.state?.toUpperCase() || '',
        postalCode: report.projectAddress?.postalCode || '',
      },
    },
  };

  // Transform invoices to match the expected interface
  const transformedInvoices: BaseInvoiceDTO[] = invoices.map(invoice => ({
    id: invoice.id,
    invoice: {
      id: invoice.invoice.id,
      number: invoice.invoice.number,
      invoiceDate: invoice.invoice.invoiceDate,
      invoiceAmount: invoice.invoice.invoiceAmount,
      paidAmount: invoice.invoice.paidAmount,
    },
    recepient: invoice.recepient,
    recepientAddress: invoice.recepientAddress,
    projectName: invoice.projectName,
    projectAddress: invoice.projectAddress,
    service: invoice.service,
    createdOn: invoice.createdOn,
    updatedOn: invoice.updatedOn,
  }));

  return (
    <InvoicePage
      reportData={reportData}
      invoices={transformedInvoices}
      onExportInvoice={handleExportAndSave}
      onExportPastInvoice={handleExport}
      onDeleteInvoice={handleDeleteInvoice}
      title={`Invoice / ${report.name || ''}`}
      isExportMode={true}
      service='Construction Cost Review'
    />
  );
};

export default CCReportInvoicePage;
